import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { RotateCw, Clock, AlertCircle, ChevronRight } from 'lucide-react';
import { API_BASE } from '../services/apiBase';

const API    = API_BASE;
const TENANT = '569593dd-38e2-4d69-bdca-5d07be8b46f1';

function authHeaders() {
  return { Authorization: 'Bearer ' + localStorage.getItem('access_token') };
}

function fmtDuration(secs) {
  if (!secs) return '--:--';
  const m = Math.floor(secs / 60), s = secs % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

function fmtTime(date) {
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
}

const STATUS_STYLE = {
  clean:   { bg: 'rgba(34,197,94,0.12)',  border: 'rgba(34,197,94,0.3)',  color: '#22c55e', label: 'CLEAN' },
  warning: { bg: 'rgba(245,158,11,0.12)', border: 'rgba(245,158,11,0.3)', color: '#f59e0b', label: 'WARNING' },
  forced:  { bg: 'rgba(239,68,68,0.12)',  border: 'rgba(239,68,68,0.3)',  color: '#ef4444', label: 'FORCED' },
};

const TYPE_STYLE = {
  music:   { bg: 'rgba(47,129,247,0.15)',  color: '#58a6ff', label: 'MUSIC' },
  jingle:  { bg: 'rgba(139,92,246,0.15)', color: '#a78bfa', label: 'JINGLE' },
  ad:      { bg: 'rgba(249,115,22,0.15)', color: '#fb923c', label: 'AD' },
  djlink:  { bg: 'rgba(20,184,166,0.15)', color: '#2dd4bf', label: 'DJ LINK' },
  sweeper: { bg: 'rgba(139,92,246,0.15)', color: '#a78bfa', label: 'SWEEPER' },
  liner:   { bg: 'rgba(236,72,153,0.15)', color: '#f472b6', label: 'LINER' },
};

function TypeBadge({ type }) {
  const s = TYPE_STYLE[type] || TYPE_STYLE.music;
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: 4,
      background: s.bg, color: s.color,
      fontSize: 10, fontWeight: 700, letterSpacing: '0.05em',
    }}>{s.label}</span>
  );
}

function StatusBadge({ status }) {
  const s = STATUS_STYLE[status] || STATUS_STYLE.clean;
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: 4,
      background: s.bg, border: '1px solid ' + s.border, color: s.color,
      fontSize: 10, fontWeight: 700, letterSpacing: '0.05em',
    }}>{s.label}</span>
  );
}

function ContextMenu({ x, y, item, onReplace, onClose }) {
  const ref = useRef(null);

  useEffect(() => {
    function handle(e) { if (ref.current && !ref.current.contains(e.target)) onClose(); }
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [onClose]);

  return (
    <div ref={ref} style={{
      position: 'fixed', left: x, top: y, zIndex: 9999,
      background: '#1c2333', border: '1px solid #30363d', borderRadius: 8,
      padding: '4px 0', minWidth: 180, boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
    }}>
      <div style={{ padding: '6px 14px 4px', fontSize: 11, color: '#8b949e', borderBottom: '1px solid #30363d', marginBottom: 4 }}>
        {item.track ? `${item.track.artist} – ${item.track.title}`.slice(0, 32) : 'Empty slot'}
      </div>
      {item.category_id && item.track && (
        <button onClick={() => { onReplace(item); onClose(); }} style={{
          display: 'block', width: '100%', textAlign: 'left',
          padding: '7px 14px', background: 'transparent', border: 'none',
          color: '#e6edf3', cursor: 'pointer', fontSize: 13,
        }}
        onMouseEnter={e => e.currentTarget.style.background = '#21262d'}
        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
          <RotateCw size={13} style={{ marginRight: 8, verticalAlign: 'middle', color: '#58a6ff' }} />
          Replace track
        </button>
      )}
      <button onClick={onClose} style={{
        display: 'block', width: '100%', textAlign: 'left',
        padding: '7px 14px', background: 'transparent', border: 'none',
        color: '#8b949e', cursor: 'pointer', fontSize: 13,
      }}
      onMouseEnter={e => e.currentTarget.style.background = '#21262d'}
      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
        Cancel
      </button>
    </div>
  );
}

function QueueRow({ item, index, estimatedTime, onContextMenu, replacing }) {
  const [hovered, setHovered] = useState(false);
  const isFirst = index === 0;

  return (
    <tr
      onContextMenu={e => { e.preventDefault(); onContextMenu(e, item); }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: isFirst
          ? 'rgba(47,129,247,0.07)'
          : hovered ? '#21262d' : 'transparent',
        borderBottom: '1px solid #21262d',
        transition: 'background 0.1s',
        opacity: replacing ? 0.4 : 1,
      }}
    >
      {/* Position */}
      <td style={{ padding: '10px 12px', width: 44, textAlign: 'center' }}>
        {isFirst ? (
          <div style={{
            width: 8, height: 8, borderRadius: '50%',
            background: '#22c55e', margin: '0 auto',
            boxShadow: '0 0 6px #22c55e',
          }} />
        ) : (
          <span style={{ fontSize: 12, color: '#484f58', fontVariantNumeric: 'tabular-nums' }}>
            {item.position}
          </span>
        )}
      </td>

      {/* Time */}
      <td style={{ padding: '10px 12px', width: 72 }}>
        <span style={{
          fontSize: 13, fontVariantNumeric: 'tabular-nums',
          color: isFirst ? '#58a6ff' : '#8b949e', fontWeight: isFirst ? 700 : 400,
        }}>
          {fmtTime(estimatedTime)}
        </span>
      </td>

      {/* Track */}
      <td style={{ padding: '10px 12px' }}>
        {item.track ? (
          <>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#e6edf3', lineHeight: '18px' }}>
              {item.track.title || '—'}
            </div>
            <div style={{ fontSize: 11, color: '#8b949e', marginTop: 1 }}>
              {item.track.artist || '—'}
            </div>
          </>
        ) : (
          <span style={{ fontSize: 12, color: '#484f58', fontStyle: 'italic' }}>
            {item.label || 'Empty slot'}
          </span>
        )}
      </td>

      {/* Type */}
      <td style={{ padding: '10px 12px', width: 90 }}>
        <TypeBadge type={item.slot_type} />
      </td>

      {/* Category */}
      <td style={{ padding: '10px 12px', width: 140 }}>
        <span style={{ fontSize: 12, color: '#8b949e' }}>
          {item.category_name || '—'}
        </span>
      </td>

      {/* Duration */}
      <td style={{ padding: '10px 12px', width: 70, textAlign: 'right' }}>
        <span style={{ fontSize: 12, color: '#484f58', fontVariantNumeric: 'tabular-nums' }}>
          {fmtDuration(item.track?.duration_seconds)}
        </span>
      </td>

      {/* Status */}
      <td style={{ padding: '10px 12px', width: 100 }}>
        <StatusBadge status={item.burn_status || 'clean'} />
      </td>

      {/* Actions hint */}
      <td style={{ padding: '10px 8px', width: 36, textAlign: 'center' }}>
        {hovered && item.category_id && item.track && (
          <span style={{ fontSize: 11, color: '#484f58' }}>
            <ChevronRight size={13} />
          </span>
        )}
      </td>
    </tr>
  );
}

export default function SchedulePage() {
  const [stores, setStores] = useState([]);
  const [storeId, setStoreId] = useState('');
  const [queue, setQueue] = useState([]);
  const [scheduleInfo, setScheduleInfo] = useState(null);
  const [generatedAt, setGeneratedAt] = useState(null);
  const [loading, setLoading] = useState(false);
  const [queueSize, setQueueSize] = useState(30);
  const [contextMenu, setContextMenu] = useState(null); // { x, y, item }
  const [replacing, setReplacing] = useState(null); // position index being replaced

  // Load stores on mount
  useEffect(() => {
    axios.get(`${API}/tenants/${TENANT}/stores`, { headers: authHeaders() })
      .then(r => {
        const list = r.data?.items || r.data || [];
        setStores(list);
        if (list.length > 0) setStoreId(list[0].id);
      })
      .catch(console.error);
  }, []);

  const generate = useCallback(async () => {
    setLoading(true);
    setContextMenu(null);
    try {
      const params = new URLSearchParams({ tenant_id: TENANT, size: queueSize });
      if (storeId) params.append('store_id', storeId);
      const r = await axios.get(`${API}/broadcast-queue?${params}`, { headers: authHeaders() });
      setQueue(r.data.queue || []);
      setScheduleInfo(r.data.schedule);
      setGeneratedAt(r.data.generated_at);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  }, [storeId, queueSize]);

  // Auto-generate when store changes (if already has a store)
  useEffect(() => {
    if (storeId) generate();
  }, [storeId]); // generate is stable enough — intentionally omitted

  const handleContextMenu = (e, item) => {
    setContextMenu({ x: e.clientX, y: e.clientY, item });
  };

  const handleReplace = async (item) => {
    if (!item.category_id || !item.track) return;
    setReplacing(item.position);
    try {
      const params = new URLSearchParams({
        category_id: item.category_id,
        exclude_id: item.track.id,
        tenant_id: TENANT,
        limit: 5,
      });
      const r = await axios.get(`${API}/broadcast-queue/alternatives?${params}`, { headers: authHeaders() });
      const alts = r.data.alternatives || [];
      if (alts.length === 0) {
        alert('No alternative tracks available in this category.');
      } else {
        const alt = alts[0];
        setQueue(prev => prev.map(q =>
          q.position === item.position
            ? { ...q, track: { ...q.track, ...alt }, burn_status: 'clean' }
            : q
        ));
      }
    } catch (e) {
      console.error(e);
    }
    setReplacing(null);
  };

  // Calculate estimated start times for each track
  const startTime = generatedAt ? new Date(generatedAt) : new Date();
  const estimatedTimes = [];
  let cursor = startTime.getTime();
  for (const item of queue) {
    estimatedTimes.push(new Date(cursor));
    cursor += (item.track?.duration_seconds || 210) * 1000;
  }

  const stats = {
    clean:   queue.filter(q => q.burn_status === 'clean').length,
    warning: queue.filter(q => q.burn_status === 'warning').length,
    forced:  queue.filter(q => q.burn_status === 'forced').length,
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', background: '#0d1117', color: '#e6edf3', fontFamily: 'system-ui, sans-serif', overflow: 'hidden' }}>

      {/* Header */}
      <div style={{ padding: '16px 24px', borderBottom: '1px solid #30363d', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#e6edf3' }}>Broadcast Scheduler</h1>
          <p style={{ margin: '2px 0 0', fontSize: 12, color: '#8b949e' }}>
            Generated playout queue with burnout status — right-click any track to replace
          </p>
        </div>

        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          {/* Queue size */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: 12, color: '#8b949e' }}>Tracks:</span>
            <select
              value={queueSize}
              onChange={e => setQueueSize(Number(e.target.value))}
              style={{ background: '#161b22', border: '1px solid #30363d', borderRadius: 6, padding: '5px 8px', color: '#e6edf3', fontSize: 13 }}
            >
              {[10, 20, 30, 50].map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>

          {/* Store selector */}
          <select
            value={storeId}
            onChange={e => setStoreId(e.target.value)}
            style={{ background: '#161b22', border: '1px solid #30363d', borderRadius: 6, padding: '6px 12px', color: '#e6edf3', fontSize: 13 }}
          >
            {stores.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>

          {/* Refresh */}
          <button
            onClick={generate}
            disabled={loading}
            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 16px', borderRadius: 6, border: 'none', background: '#2f81f7', color: '#fff', cursor: loading ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 600, opacity: loading ? 0.7 : 1 }}
          >
            <RotateCw size={14} style={{ animation: loading ? 'spin 1s linear infinite' : 'none' }} />
            {loading ? 'Generating…' : 'Generate'}
          </button>
        </div>
      </div>

      {/* Active schedule banner */}
      {queue.length > 0 && (
        <div style={{ padding: '8px 24px', background: '#161b22', borderBottom: '1px solid #30363d', display: 'flex', gap: 20, alignItems: 'center', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Clock size={13} color="#58a6ff" />
            <span style={{ fontSize: 12, color: '#8b949e' }}>
              {scheduleInfo
                ? <><span style={{ color: '#58a6ff', fontWeight: 600 }}>{scheduleInfo.clock_name || scheduleInfo.name}</span> · {scheduleInfo.start_time?.slice(0, 5)} – {scheduleInfo.end_time?.slice(0, 5)}</>
                : <span style={{ color: '#f59e0b' }}>No active schedule — random fallback</span>}
            </span>
          </div>
          <div style={{ height: 12, width: 1, background: '#30363d' }} />
          <div style={{ display: 'flex', gap: 14, fontSize: 12 }}>
            <span><span style={{ color: '#22c55e', fontWeight: 700 }}>{stats.clean}</span> <span style={{ color: '#8b949e' }}>clean</span></span>
            {stats.warning > 0 && <span><span style={{ color: '#f59e0b', fontWeight: 700 }}>{stats.warning}</span> <span style={{ color: '#8b949e' }}>warning</span></span>}
            {stats.forced > 0 && <span><span style={{ color: '#ef4444', fontWeight: 700 }}>{stats.forced}</span> <span style={{ color: '#8b949e' }}>forced</span></span>}
          </div>
          {generatedAt && (
            <>
              <div style={{ height: 12, width: 1, background: '#30363d' }} />
              <span style={{ fontSize: 11, color: '#484f58' }}>
                Generated {new Date(generatedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </span>
            </>
          )}
        </div>
      )}

      {/* Queue table */}
      <div style={{ flex: 1, overflow: 'auto' }}>
        {queue.length === 0 && !loading ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 12, color: '#484f58' }}>
            <AlertCircle size={40} />
            <p style={{ margin: 0, fontSize: 14 }}>Select a store and click Generate to build the broadcast queue</p>
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #30363d', background: '#161b22' }}>
                <th style={{ padding: '8px 12px', textAlign: 'center', width: 44, fontSize: 11, color: '#8b949e', fontWeight: 600 }}>#</th>
                <th style={{ padding: '8px 12px', width: 72, fontSize: 11, color: '#8b949e', fontWeight: 600, textAlign: 'left' }}>TIME</th>
                <th style={{ padding: '8px 12px', fontSize: 11, color: '#8b949e', fontWeight: 600, textAlign: 'left' }}>TRACK</th>
                <th style={{ padding: '8px 12px', width: 90, fontSize: 11, color: '#8b949e', fontWeight: 600, textAlign: 'left' }}>TYPE</th>
                <th style={{ padding: '8px 12px', width: 140, fontSize: 11, color: '#8b949e', fontWeight: 600, textAlign: 'left' }}>CATEGORY</th>
                <th style={{ padding: '8px 12px', width: 70, fontSize: 11, color: '#8b949e', fontWeight: 600, textAlign: 'right' }}>DUR</th>
                <th style={{ padding: '8px 12px', width: 100, fontSize: 11, color: '#8b949e', fontWeight: 600, textAlign: 'left' }}>STATUS</th>
                <th style={{ width: 36 }} />
              </tr>
            </thead>
            <tbody>
              {queue.map((item, i) => (
                <QueueRow
                  key={item.position}
                  item={item}
                  index={i}
                  estimatedTime={estimatedTimes[i]}
                  onContextMenu={handleContextMenu}
                  replacing={replacing === item.position}
                />
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Context menu */}
      {contextMenu && (
        <ContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          item={contextMenu.item}
          onReplace={handleReplace}
          onClose={() => setContextMenu(null)}
        />
      )}

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}
